import type { Runtime } from './host';

/**
 * Runtime capabilities — a typed registry that decouples screens from
 * direct runtime checks (`useIsTma`, `isTelegramWebApp`, etc.). Each
 * capability names a feature or behavior that may be available in one
 * runtime but not the other. Components ask `useCapability('haptics')`
 * instead of inferring runtime themselves.
 *
 * Source-of-truth audit: `docs/superpowers/specs/2026-05-05-capability-map.md`
 * Decisions: `docs/superpowers/specs/2026-05-05-decisions.md`
 */
export type Capability =
  // Telegram chrome
  | 'telegramBackButton'
  | 'swipeBackGesture'
  | 'closingConfirmation'
  | 'themeChrome'
  | 'fullscreenExpand'
  | 'safeAreaInsets'

  // Telegram I/O actions (gate `useRuntimeActions` branches)
  | 'haptics'
  | 'starsPayment'
  | 'nativeShare'
  | 'preparedMessage'
  | 'tmaConfirmPopup'
  | 'tmaAlertPopup'
  | 'inlineLinkOpen'
  | 'telegramLinkOpen'
  | 'closeApp'

  // Auth / identity
  | 'initDataAuth'
  | 'loginWidgetAuth'
  | 'steamOpenIdAuth'
  | 'logoutAction'

  // Layout chrome
  | 'desktopLayout'
  | 'desktopHeader'
  | 'mobileTabBar'

  // Per-task gates (D4: split, NOT a single coarse `tasksTmaSet`)
  | 'channelSubscriptionTask'
  | 'channelBoostTask'
  | 'profileBioTask'

  // Screen / feature gates
  | 'recruiterTmaOnly'
  | 'supportChatWidget'
  | 'profileShare'
  | 'referralShare'
  | 'matchStreamLink'

  // Public-page access (web only — these are pages anonymous web users see)
  | 'publicCaseListView'
  | 'publicCaseDetailView'
  | 'publicProfileView'
  | 'publicItemView'
  | 'publicItemInCaseView';

export const CAPABILITIES: Record<Runtime, ReadonlySet<Capability>> = {
  tma: new Set<Capability>([
    'telegramBackButton',
    'swipeBackGesture',
    'closingConfirmation',
    'themeChrome',
    'fullscreenExpand',
    'safeAreaInsets',

    'haptics',
    'starsPayment',
    'nativeShare',
    'preparedMessage',
    'tmaConfirmPopup',
    'tmaAlertPopup',
    'inlineLinkOpen',
    'telegramLinkOpen',
    'closeApp',

    'initDataAuth',

    'desktopLayout',
    'desktopHeader',
    'mobileTabBar',

    'channelSubscriptionTask',
    'channelBoostTask',
    'profileBioTask',

    'recruiterTmaOnly',
    'profileShare',
    'referralShare',
    'matchStreamLink',
  ]),

  web: new Set<Capability>([
    'loginWidgetAuth',
    'steamOpenIdAuth',
    'logoutAction',

    'desktopLayout',
    'desktopHeader',
    'mobileTabBar',

    'supportChatWidget',

    'profileShare',
    'referralShare',
    'matchStreamLink',

    'publicCaseListView',
    'publicCaseDetailView',
    'publicProfileView',
    'publicItemView',
    'publicItemInCaseView',
  ]),
};
